import { Unit } from '../fields/NumericField';
import type { ExpectTrue } from '../tests';
import { includesEnumValue } from '../utils';
import { toBigWithDefault } from '../utils/number';
import type { CustomerDealSummary } from './CustomerOrderSummary';
import { getFixingIndicesList, type FixingDetails } from './FixingDetails';
import type { NegotiationParameters } from './NegotiationParameters';
import {
  QuoteStatusEnum,
  RFQProtocolEnum,
  type CustomerRiskApprovalStatusEnum,
  type CustomerWorkflowEnum,
  type ICustomerQuote,
  type NegotiationStatusEnum,
  type PricingModeEnum,
  type SideEnum,
} from './types';

export const isCustomerQuote = (entity: any): entity is CustomerQuote => {
  return entity instanceof CustomerQuote;
};

const COMPLETE_CUSTOMER_QUOTE_STATUSES: QuoteStatusEnum[] = ['Canceled', 'Closed', 'Filled', 'PendingFix', 'Rejected'];

export class CustomerQuote {
  static readonly rowID = 'RFQID';
  static readonly defaultColumns = [
    'Side',
    'Symbol',
    'OrderQty',
    'User',
    'Counterparty',
    'QuoteStatus',
    'RFQID',
  ] satisfies (keyof CustomerQuote)[];

  AmountCurrency!: string;

  BidAmt?: string;

  BidPx?: string;

  BidQty?: string;

  Counterparty!: string;

  Currency!: string;

  CustomerUser!: string;

  // [MKTP-1129] EndTime can be null for Customer Quotes
  EndTime!: string | null;

  OrderQty!: string;

  OfferAmt?: string;

  OfferPx?: string;

  OfferQty?: string;

  Parameters!: { [key: string]: string | string[] | boolean | undefined };
  NegotiationParameters?: NegotiationParameters;

  MarketAccount!: string;

  QuoteID!: string;

  QuoteReqID!: string;

  QuoteStatus!: QuoteStatusEnum;

  RFQID!: string;

  Side?: SideEnum;

  SubmitTime!: string;

  Symbol?: string;

  Text?: string;

  Timestamp!: string;

  TradedAmt?: string;

  TradedPx?: string;

  TradedQty?: string;

  TradedSide?: SideEnum;

  User?: string;

  ValidUntilTime?: string;

  ExpectedHedgeOfferPx?: string;

  ExpectedHedgeBidPx?: string;

  EffectiveBidSpread?: string;

  EffectiveOfferSpread?: string;

  OrigQuoteReqID!: string;

  TransactTime!: string;
  InternalText?: string | undefined;
  InternalQuoteRequestRejectReason?: string | undefined;
  QuoteRequestRejectReason?: string | undefined;
  SubAccount?: string | undefined;
  MessageID?: unknown;
  ReferenceQuoteID?: unknown;
  Group?: string | undefined;
  Comments?: string | undefined;
  ExternalComments?: string | undefined;
  InternalMarketErrors?: unknown;
  EventIndicator?: unknown;
  MinSizeIncrement?: string;

  PricingMode?: PricingModeEnum;

  PricingReference?: string | undefined;

  summary?: CustomerDealSummary;

  Workflow?: CustomerWorkflowEnum;

  LegSummary?: {
    LegIndex: number;
    Symbol: string;
    BidQty: string;
    OrderQty?: string;
    OfferQty: string;
    Currency: string;
    AmountCurrency: string;
    BidPx?: string;
    OfferPx?: string;
    TradedPx?: string;
    TradedAmt?: string;
    TradedQty?: string;
    SizeRatio?: string;
    TradedSide?: SideEnum;
    PricingReference?: string;
    FixingDetails?: {
      Index: string;
      Fixing?: string;
    };
  }[];

  BuySideApprovalStatus?: CustomerRiskApprovalStatusEnum;
  SellSideApprovalStatus?: CustomerRiskApprovalStatusEnum;

  FixingDetails?: FixingDetails;
  /** An array of all the fixing indices referenced on this entity */
  fixingIndices: string[] | undefined;
  /** Fixing date across the quote's fixing details, if present */
  get fixingDate() {
    if (this.FixingDetails?.FixingDate) {
      return this.FixingDetails.FixingDate;
    }

    // For multileg quotes, assume all legs share the same fixing date and use the first present value.
    return Object.values(this.FixingDetails?.MultilegFixingDetails ?? {}).at(0)?.FixingDate;
  }

  RFQProtocol?: RFQProtocolEnum;
  ExternalGroup?: string;
  SettleDate?: string;
  ClearingMarket?: string;
  ClearingAccount?: string;

  NegotiationStatus?: NegotiationStatusEnum;

  OrgID!: number;

  /** Rebase multiplier for the underlying TokenizedAsset at quote submission (from API). */
  RebaseMultiplier?: string;

  postTradeClearingSymbol?: string;
  postTradeClearingID?: string;

  get statusText() {
    return this.InternalText ?? this.Text;
  }

  get unit() {
    return this.PricingMode === 'SpreadToFixing' ? Unit.Bps : Unit.Decimal;
  }

  /** If the unit is bps, will return OfferPx in BPS */
  get offerPxBps() {
    return this.unit === Unit.Bps ? toBigWithDefault(this.OfferPx, 0).times(10_000).toFixed() : undefined;
  }

  /** If the unit is bps, will return BidPx in BPS */
  get bidPxBps() {
    return this.unit === Unit.Bps ? toBigWithDefault(this.BidPx, 0).times(10_000).toFixed() : undefined;
  }

  /** TradedSide, or if that does not exist, Side */
  get tradedSideOrSide() {
    return this.TradedSide ?? this.Side;
  }

  /** BidQty, or if that does not exist, OrderQty */
  get bidQtyOrOrderQty() {
    return this.BidQty ?? this.OrderQty;
  }

  /** OfferQty, or if that does not exist, OrderQty */
  get offerQtyOrOrderQty() {
    return this.OfferQty ?? this.OrderQty;
  }

  /** Returns whether the quoting process is complete. Is different than isTerminal. For example, PendingFix is not a terminal state in
   * the CustomerQuote state machine.
   */
  get isComplete(): boolean {
    return COMPLETE_CUSTOMER_QUOTE_STATUSES.includes(this.QuoteStatus);
  }

  get canModifyFixing(): boolean {
    return this.QuoteStatus === 'Filled' && this.PricingMode === 'SpreadToFixing' && this.FixingDetails != null;
  }

  get isMultitoken() {
    // We still want to let thru single-token compound instrument quotes (which have LegSummary)
    return this.LegSummary && this.LegSummary.length > 1;
  }

  get isOpen() {
    return includesEnumValue([QuoteStatusEnum.PendingNew, QuoteStatusEnum.Open], this.QuoteStatus);
  }

  get canRespondToQuote(): boolean {
    return this.isOpen && this.RFQProtocol === RFQProtocolEnum.Manual;
  }

  get canFixQuote(): boolean {
    return this.QuoteStatus === QuoteStatusEnum.PendingFix;
  }

  /**
   * CustomerQuotes that are compound do not have a top-level Symbol, instead they have LegSummary[].Symbol
   * This getter exposes whichever of these two exists
   */
  get symbolOrSymbols(): string | string[] | undefined {
    return this.Symbol ?? this.legSymbols;
  }

  /** If there are LegSummary records, will return the Symbols found in those records */
  get legSymbols(): string[] | undefined {
    return this.LegSummary?.map(ls => ls.Symbol);
  }

  constructor(data: Omit<CustomerQuote, CustomerQuoteGetters>) {
    Object.assign(this, data);
    this.fixingIndices = getFixingIndicesList(this.FixingDetails);

    // For some cases the backend shoves a few fields into a JSON blob in the comments here. If so, try to parse and put into distinct properties on the class.
    // Throwing errors is expensive. We try to avoid this in this constructor by checking that it seems like a stringified json blob before we attempt to parse.
    if (this.Comments && this.Comments.startsWith('{')) {
      try {
        const parsedComments: CommentsObject | undefined = JSON.parse(this.Comments);
        this.postTradeClearingSymbol = parsedComments?.PostTradeClearingSymbol;
        this.postTradeClearingID = parsedComments?.PostTradeClearingID;
      } catch (e) {
        this.postTradeClearingSymbol = undefined;
        this.postTradeClearingID = undefined;
      }
    }
  }
}

interface CommentsObject {
  PostTradeClearingSymbol?: string;
  PostTradeClearingID?: string;
}

type CustomerQuoteGetters = keyof Pick<
  CustomerQuote,
  | 'tradedSideOrSide'
  | 'bidQtyOrOrderQty'
  | 'offerQtyOrOrderQty'
  | 'unit'
  | 'offerPxBps'
  | 'bidPxBps'
  | 'isComplete'
  | 'canModifyFixing'
  | 'postTradeClearingSymbol'
  | 'postTradeClearingID'
  | 'statusText'
  | 'isOpen'
  | 'canFixQuote'
  | 'canRespondToQuote'
  | 'symbolOrSymbols'
  | 'legSymbols'
  | 'fixingIndices'
  | 'fixingDate'
  | 'isMultitoken'
>;

// TYPE LEVEL TESTS
type _Expect_CustomerQuote_To_Only_Have_Keys_From_ICustomerQuote = ExpectTrue<
  {
    [K in keyof CustomerQuote & string]: K extends keyof ICustomerQuote ? true : K;
  }[Exclude<
    keyof CustomerQuote,
    | 'summary'
    | 'TransactTime' /* TODO Remove TransactTime from this type, it shouldn't be here */
    | 'FixingDetails'
    | 'NegotiationStatus'
    | 'LegSummary'
    | 'BidQty'
    | 'OfferQty'
    | 'MinSizeIncrement'
    | 'NegotiationParameters'
    | 'ClearingMarket'
    | 'ClearingAccount'
    | CustomerQuoteGetters
  >]
>;
type _Expect_All_Keys_In_ICustomerQuote_To_Be_In_CustomerQuote = ExpectTrue<
  {
    [K in keyof ICustomerQuote & string]: K extends keyof CustomerQuote ? true : K;
  }[Exclude<keyof ICustomerQuote, never>]
>;
